import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js";

// Generic inline creator controller for quickly creating new records.
// Can be used for tasks, subtasks, or any other resource.
//
// Values:
//   url: The POST endpoint URL
//   extraParams: JSON object of additional parameters to include in the request
//   fieldName: The name field key (default: "name")
//   modelName: The model key for params (default: "record")
//   successMessage: Flash message on success (default: "Created")
//
export default class extends Controller {
  static targets = ["input", "container", "addButton"]
  static values = { 
    url: String,
    extraParams: { type: Object, default: {} },
    fieldName: { type: String, default: "name" },
    modelName: { type: String, default: "record" },
    successMessage: { type: String, default: "Created" }
  }

  connect() {
    this.isCreating = false
    this.isSaving = false
    
    // Listen for external trigger events (e.g., from header + button)
    this.handleExternalTrigger = this.handleExternalTrigger.bind(this)
    this.element.addEventListener('inline-creator:trigger', this.handleExternalTrigger)
  }
  
  disconnect() {
    this.element.removeEventListener('inline-creator:trigger', this.handleExternalTrigger)
  }
  
  handleExternalTrigger() {
    this.showInput()
  }

  startCreating(event) {
    event.preventDefault()
    this.showInput()
  }

  showInput() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.style.display = 'none'
    }
    
    if (this.hasContainerTarget) {
      this.containerTarget.style.display = 'block'
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
      this.inputTarget.focus()
    }
    
    this.isCreating = true
    
    // Scroll the input into view
    setTimeout(() => {
      if (this.hasContainerTarget) {
        this.containerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }, 50)
  }

  hideInput() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.style.display = ''
    }
    
    if (this.hasContainerTarget) {
      this.containerTarget.style.display = 'none'
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
    
    this.isCreating = false
  }

  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.save()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancel()
    }
  }

  handleBlur(event) {
    // Small delay to allow click events on save/cancel buttons to fire first
    setTimeout(() => {
      if (!this.isCreating) return
      
      const value = this.inputTarget.value.trim()
      if (value === '') {
        this.hideInput()
      }
    }, 150)
  }

  save() {
    // Prevent duplicate submissions
    if (this.isSaving) return
    
    const value = this.inputTarget.value.trim()
    
    if (value === '') {
      this.hideInput()
      return
    }
    
    this.isSaving = true
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    
    // Build request body with model name and field name
    const requestBody = {
      inline: true
    }
    
    requestBody[this.modelNameValue] = {
      [this.fieldNameValue]: value,
      ...this.extraParamsValue
    }
    
    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      if (response.ok) {
        return response.text()
      } else {
        throw new Error('Failed to create')
      }
    })
    .then(html => {
      // Process the turbo stream response
      Turbo.renderStreamMessage(html)
      
      flash(this.successMessageValue, "success")
      
      // Clear input and start a new one
      this.inputTarget.value = ''
      this.inputTarget.focus()
      
      // Scroll the input into view so user can continue adding
      setTimeout(() => {
        this.containerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 100)
    })
    .catch(error => {
      console.error('Error creating:', error)
      flash("Failed to create", "error")
    })
    .finally(() => {
      this.isSaving = false
    })
  }

  cancel() {
    this.hideInput()
  }
}
